<script setup lang="ts">
  import { ref, onMounted } from 'vue';
  import { useUi } from '@/composition/use-ui';

  const props = defineProps<{
    error?: Error | null;
    onRetry?: () => void;
  }>();

  const ui = useUi();
  const retrying = ref(false);

  // Auto-retry count
  const retryCount = ref(0);
  const maxRetries = 3;

  const handleRetry = async () => {
    retrying.value = true;
    retryCount.value++;

    try {
      if (props.onRetry) {
        await props.onRetry();
      } else {
        // Fallback to page reload
        window.location.reload();
      }
    } catch (error) {
      console.error('Retry failed:', error);
    } finally {
      retrying.value = false;
    }
  };

  // Auto-retry on mount
  onMounted(() => {
    if (retryCount.value < maxRetries) {
      setTimeout(() => {
        handleRetry();
      }, 2000); // Wait 2 seconds before first auto-retry
    }
  });

  // Determine error message
  const getErrorMessage = () => {
    if (!props.error) {
      return 'No se pudo conectar con el servidor';
    }

    const message = props.error.message?.toLowerCase() || '';

    if (message.includes('network') || message.includes('fetch')) {
      return 'Error de conexión. Verifique su conexión a internet';
    }

    if (message.includes('timeout')) {
      return 'La conexión tardó demasiado. El servidor podría estar sobrecargado';
    }

    if (message.includes('500') || message.includes('internal')) {
      return 'Error del servidor. Por favor, intente más tarde';
    }

    return 'No se pudo conectar con el servidor';
  };
</script>

<template>
  <div class="connection-error-container">
    <v-container>
      <v-row justify="center" align="center" style="min-height: 100vh">
        <v-col cols="12" sm="8" md="6" lg="4">
          <v-card class="error-card">
            <v-card-text class="text-center">
              <!-- Logo -->
              <img
                src="/assets/img/logos/logotext@25.png"
                alt="QueRestó"
                class="logo"
              />

              <!-- Error Icon -->
              <v-icon size="80" color="error" class="error-icon">
                mdi-wifi-off
              </v-icon>

              <!-- Error Message -->
              <h2 class="error-title">Error de Conexión</h2>

              <p class="error-message">
                {{ getErrorMessage() }}
              </p>

              <!-- Retry Information -->
              <div v-if="retryCount > 0" class="retry-info">
                <v-chip small outlined color="grey">
                  Intento {{ retryCount }} de {{ maxRetries }}
                </v-chip>
              </div>

              <!-- Actions -->
              <div class="actions">
                <v-btn
                  color="primary"
                  :loading="retrying"
                  :disabled="retrying"
                  @click="handleRetry"
                  rounded
                  depressed
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Reintentar
                </v-btn>

                <v-btn
                  text
                  color="grey"
                  @click="$router.push('/login')"
                  class="secondary-action"
                  v-if="$route.path !== '/login'"
                >
                  Ir al inicio de sesión
                </v-btn>
              </div>

              <!-- Help Text -->
              <div class="help-text">
                <p>Si el problema persiste:</p>
                <ul>
                  <li>Verifique su conexión a internet</li>
                  <li>Intente limpiar el caché del navegador (Ctrl+Shift+R)</li>
                  <li>Contacte a soporte si continúa el error</li>
                </ul>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<style scoped lang="scss">
  .connection-error-container {
    @apply tw-min-h-screen tw-bg-gray-50;

    .error-card {
      @apply tw-shadow-lg tw-border-0;

      .logo {
        @apply tw-max-w-[200px] tw-h-auto tw-mb-6;
      }

      .error-icon {
        @apply tw-mb-4;
        animation: pulse 2s infinite;
      }

      .error-title {
        @apply tw-text-2xl tw-font-light tw-text-gray-800 tw-mb-2;
      }

      .error-message {
        @apply tw-text-gray-600 tw-mb-4;
      }

      .retry-info {
        @apply tw-mb-4;
      }

      .actions {
        @apply tw-mb-6;

        .secondary-action {
          @apply tw-mt-2;
        }
      }

      .help-text {
        @apply tw-text-sm tw-text-gray-500 tw-text-left;
        @apply tw-bg-gray-50 tw-rounded tw-p-4 tw-mt-6;

        p {
          @apply tw-font-medium tw-mb-2;
        }

        ul {
          @apply tw-list-disc tw-list-inside tw-space-y-1;
        }
      }
    }
  }

  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }
</style>
